// React and MUI imports for hooks, layout, and UI
import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Api from '../../Api'; // API utility for backend calls
import { Box, Typography, Container, useMediaQuery, useTheme, CssBaseline } from '@mui/material';
import Header from '../../commons/Header'; // App header
import Footer from '../../commons/Footer'; // App footer

/**
 * EmailChangeActivation
 * Handles the process of confirming a user's new email address after they click the activation link.
 * Verifies the code from the URL, then redirects to login or home.
 */
function EmailChangeActivation() {
  // Get URL search params (e.g., ?code=...)
  let [params, SetParams] = useSearchParams();
  // React Router navigation function
  let navigate = useNavigate();
  // Activation code from email link
  let code = params.get('code');
  // Access the current MUI theme object
  const theme = useTheme();
  // Determine if the screen is mobile size
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // On mount, verify the email change code
  useEffect(() => {
    // Call backend API to confirm the new email
    Api.confirmNewEmail(code)
      .then((res) => {
        // On success, redirect to login (user should log in with new email)
        navigate('/login');
      })
      .catch((err) => {
        // On error, redirect to home
        navigate('/home');
      });
  }, []); // Run once on mount

  // Style for the title text
  const TitleStyle = {
    fontFamily: 'Aventa',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: isMobile ? '31px' : '41px',
    lineHeight: isMobile ? '41px' : '54px',
    color: theme.palette.mode == 'light' ? '#002136' : '#FFFFFF',
    textAlign: 'left',
  };

  // Style for the main vertical flex box
  const MainStyledBox = {
    display: 'flex',
    flexDirection: 'column',
  };

  // Style for the left divider (desktop only)
  const StyledDividerLeft = {
    borderLeft: isMobile ? ' ' : theme.palette.mode == 'light' ? '1px solid rgba(0, 33, 54, 0.2)' : '1px solid  rgba(255, 255, 255, 0.2)',
    paddingLeft: isMobile ? ' ' : '40px',
    marginTop: isMobile ? '40px' : '140px',
    width: isMobile ? '100%' : '50%',
  };

  // Style for the main wrapper, with background image based on theme and device
  const StyledBoxMainWrapper = {
    background: theme.palette.mode == 'light' ? (isMobile ? 'url(res/SignInBGLightMobile.svg) no-repeat top' : 'url(res/SignInBGLight.svg) no-repeat top') : isMobile ? 'url(res/SignInBGDarkMobile.svg) no-repeat top' : 'url(res/SignInBGDark.svg) no-repeat top',
    backgroundPosition: isMobile ? 'right bottom' : 'right',
    height: isMobile ? '510px' : '570px',
  };

  return (
    <>
      {/* Normalize CSS for consistent look */}
      <CssBaseline />
      {/* App header (logo, nav, etc.) */}
      <Header />
      {/* Main background section with theme-based image */}
      <Box sx={StyledBoxMainWrapper}>
        <Container maxWidth="lg">
          <Box sx={StyledDividerLeft}>
            <Box>
              {/* Main content: title only */}
              <Box sx={MainStyledBox}>
                <Typography variant="h2" sx={TitleStyle}>
                  Activating New Email
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      {/* App footer (copyright, links, etc.) */}
      <Footer />
    </>
  );
}

export default EmailChangeActivation;
